import { useEffect, useMemo, useRef, useState } from "react";
import type { PublicEvent, SaveResponse, VolunteerRole } from "../../shared/types";
import { GRADE_OPTIONS, ROLE_LABELS } from "../../shared/types";
import { ApiError, getConfig, getEvents, saveVolunteer } from "../api";
import Field from "../components/Field";
import EventCard from "../components/EventCard";
import IntroCard from "../components/IntroCard";
import SummaryBar from "../components/SummaryBar";
import { computeSummary, LOCATION_FILTERS, monthLabel, weekdayOf } from "../lib/hours";
import { CONTACT } from "../content";

interface FormState {
  name: string;
  department: string;
  grade: string;
  gradeOther: string;
  phone: string;
  note: string;
  email: string;
  roles: VolunteerRole[];
}

const EMPTY: FormState = {
  name: "",
  department: "",
  grade: "",
  gradeOther: "",
  phone: "",
  note: "",
  email: "",
  roles: [],
};

function resolvedGrade(f: FormState): string {
  if (f.grade === "其他") return f.gradeOther.trim() ? `其他：${f.gradeOther.trim()}` : "其他";
  return f.grade;
}

export default function FormPage() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [config, setConfig] = useState<Awaited<ReturnType<typeof getConfig>> | null>(null);
  const [events, setEvents] = useState<PublicEvent[]>([]);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState("all");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [topError, setTopError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [result, setResult] = useState<SaveResponse["summary"] | null>(null);
  const [resultAction, setResultAction] = useState<"draft" | "submit">("draft");

  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    Promise.all([getConfig(), getEvents()])
      .then(([c, e]) => {
        setConfig(c);
        setEvents(e.events);
      })
      .catch((err) => setLoadError(err.message || "載入失敗"))
      .finally(() => setLoading(false));
  }, []);

  const minHours = config?.minHours ?? 4;
  const canInteract = !!config?.formOpen && !config?.deadlinePassed;

  const summary = useMemo(
    () => computeSummary(events, selected, minHours, form.roles),
    [events, selected, minHours, form.roles],
  );

  const grouped = useMemo(() => {
    const f = LOCATION_FILTERS.find((x) => x.key === filter) ?? LOCATION_FILTERS[0];
    const map = new Map<string, PublicEvent[]>();
    for (const ev of events) {
      if (!f.match(ev)) continue;
      if (!map.has(ev.monthGroup)) map.set(ev.monthGroup, []);
      map.get(ev.monthGroup)!.push(ev);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [events, filter]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setFieldErrors((e) => {
      if (!e[key as string] && !e.email) return e;
      const { [key as string]: _drop, email: _email, ...rest } = e;
      return rest;
    });
  }

  function toggleRole(r: VolunteerRole) {
    setForm((f) => ({
      ...f,
      roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r],
    }));
    setFieldErrors(({ roles: _r, ...rest }) => rest);
  }

  function toggleEvent(id: number) {
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function doSave(action: "draft" | "submit") {
    setSaving(true);
    setTopError(null);
    setFieldErrors({});
    try {
      const res = await saveVolunteer({
        name: form.name,
        department: form.department,
        grade: resolvedGrade(form),
        phone: form.phone,
        note: form.note,
        email: form.email || undefined,
        roles: form.roles,
        eventIds: [...selected],
        action,
      });
      setResult(res.summary);
      setResultAction(action);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (err instanceof ApiError) {
        setTopError(err.message);
        if (err.fields) setFieldErrors(err.fields);
      } else {
        setTopError(err instanceof Error ? err.message : "送出失敗，請稍後再試");
      }
      errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="page">載入中…</div>;
  if (loadError)
    return (
      <div className="page">
        <div className="alert err">載入失敗：{loadError}</div>
      </div>
    );

  if (result)
    return <SuccessScreen summary={result} action={resultAction} onBack={() => setResult(null)} />;

  const emailError = fieldErrors.email;

  return (
    <>
      <div className="topbar">
        <img className="logo" src="/straight-a-logo.png" alt="Straight A" />
        <span className="title">場次出席調查表</span>
      </div>

      <div className="page">
        <IntroCard />

        {!config?.formOpen && (
          <div className="alert warn">
            表單目前<strong>未開放填寫</strong>。如有疑問請聯繫主辦窗口（{CONTACT.hotline}）。
          </div>
        )}
        {config?.formOpen && config?.deadlinePassed && (
          <div className="alert warn">
            填寫已於 <strong>
              {config.deadline}（{weekdayOf(config.deadline)}）
            </strong>{" "}
            截止，資料為唯讀。如需修改請聯繫主辦窗口。
          </div>
        )}
        {config?.formOpen && !config?.deadlinePassed && config?.deadline && (
          <div className="alert info">
            填寫截止日：<strong>
              {config.deadline}（{weekdayOf(config.deadline)}，含當日）
            </strong>
            。未達時數可先儲存草稿，於截止前補選。用相同 Email 再次填寫即會更新原本的資料，不會重複建立。
          </div>
        )}

        {topError && (
          <div className="alert err" ref={errorRef}>
            {topError}
          </div>
        )}

        <div className="stepper">
          <div className={`step ${basicDone(form) ? "done" : "active"}`}>1 基本資料</div>
          <div className={`step ${selected.size > 0 ? "done" : "active"}`}>2 選擇場次</div>
          <div className={`step ${summary.met ? "done" : ""}`}>3 送出</div>
        </div>

        {/* 基本資料 */}
        <div className="card">
          <h2>基本資料</h2>

          <Field label="中文姓名" required error={fieldErrors.name} htmlFor="f-name">
            <input
              id="f-name"
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              autoComplete="name"
            />
          </Field>

          <Field
            label="聯絡 Email"
            required
            error={emailError}
            hint="請填寫可收得到通知、方便聯繫的 Email。"
            htmlFor="f-email"
          >
            <input
              id="f-email"
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="you@example.com（學校或個人皆可）"
              autoComplete="email"
            />
          </Field>

          <Field label="系所" required error={fieldErrors.department} htmlFor="f-dept">
            <input
              id="f-dept"
              type="text"
              value={form.department}
              onChange={(e) => set("department", e.target.value)}
              placeholder="例：財務金融學系"
            />
          </Field>

          <Field label="年級" required error={fieldErrors.grade} htmlFor="f-grade">
            <select id="f-grade" value={form.grade} onChange={(e) => set("grade", e.target.value)}>
              <option value="">請選擇</option>
              {GRADE_OPTIONS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
            {form.grade === "其他" && (
              <input
                type="text"
                style={{ marginTop: 8 }}
                value={form.gradeOther}
                onChange={(e) => set("gradeOther", e.target.value)}
                placeholder="請說明你的身分／年級（例：在職專班、交換生）"
              />
            )}
          </Field>

          <Field label="志工身分（可複選）" required error={fieldErrors.roles}>
            <div className="checks">
              {(Object.keys(ROLE_LABELS) as VolunteerRole[]).map((r) => (
                <label key={r} className={`check${form.roles.includes(r) ? " on" : ""}`}>
                  <input
                    type="checkbox"
                    checked={form.roles.includes(r)}
                    onChange={() => toggleRole(r)}
                  />
                  {ROLE_LABELS[r]}
                </label>
              ))}
            </div>
          </Field>

          <Field label="手機號碼" required error={fieldErrors.phone} htmlFor="f-phone">
            <input
              id="f-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              placeholder="09xx-xxx-xxx"
              autoComplete="tel"
            />
          </Field>

          <Field label="備註或特殊需求" htmlFor="f-note">
            <textarea id="f-note" value={form.note} onChange={(e) => set("note", e.target.value)} />
          </Field>

          <p className="muted">你填寫的 Email、手機與個資僅供主辦單位作業使用，不會公開顯示。</p>
        </div>

        {/* 場次選擇 */}
        <div className="card">
          <h2>選擇可出席場次</h2>
          <p className="muted">
            系統會即時計算已選服務時數。AR 伴讀每場認列 2 小時；星星國公益活動日期已確認（10 月 10/31、12 月 12/25–26），地點由主辦後續通知。
            額滿的場次仍可勾選，會依送出時間列為遞補。
          </p>

          <div className="filters">
            {LOCATION_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={filter === f.key ? "on" : ""}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {grouped.length === 0 && <p className="muted">此篩選條件下沒有場次。</p>}

          {grouped.map(([mg, list]) => (
            <div className="month-group" key={mg}>
              <div className="m-head">{monthLabel(mg)}</div>
              {list.map((ev) => (
                <EventCard
                  key={ev.id}
                  event={ev}
                  checked={selected.has(ev.id)}
                  roles={form.roles}
                  onToggle={canInteract ? toggleEvent : () => {}}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      <SummaryBar
        summary={summary}
        minHours={minHours}
        saving={saving}
        canInteract={canInteract}
        onSaveDraft={() => doSave("draft")}
        onSubmit={() => doSave("submit")}
      />
    </>
  );
}

function basicDone(f: FormState): boolean {
  return !!(
    f.name &&
    f.department &&
    f.grade &&
    (f.grade !== "其他" || f.gradeOther.trim()) &&
    f.phone &&
    f.roles.length > 0 &&
    f.email
  );
}

function SuccessScreen({
  summary,
  action,
  onBack,
}: {
  summary: SaveResponse["summary"];
  action: "draft" | "submit";
  onBack: () => void;
}) {
  return (
    <div className="page">
      <div className="card success">
        <div className="success-check">✓</div>
        <h2>{action === "submit" ? "已送出最終確認！" : "草稿已儲存"}</h2>
        <p className="center muted">
          {action === "submit"
            ? "感謝填寫，主辦單位將依結果安排人力。"
            : "資料已暫存。在截止日前，你可以用相同 Email 再次進入本表單補選或修改。"}
        </p>

        <div className="recap">
          <div>
            <strong>姓名：</strong>
            {summary.name}
          </div>
          <div>
            <strong>已選總服務時數：</strong>
            {summary.totalHours} 小時（門檻 <strong>{summary.minHours}</strong> 小時）
            {summary.totalHours >= summary.minHours ? (
              <span className="tag ok" style={{ marginLeft: 6 }}>
                已達標
              </span>
            ) : (
              <span className="tag warn" style={{ marginLeft: 6 }}>
                未達標
              </span>
            )}
          </div>
          <div>
            <strong>正取時數：</strong>
            {summary.confirmedHours} 小時
            {summary.waitlistHours > 0 && (
              <>
                {" ／ "}
                <strong>遞補時數：</strong>
                {summary.waitlistHours} 小時
              </>
            )}
          </div>
          <div>
            <strong>伴讀：</strong>
            {summary.morningCount} 場 · <strong>公益活動：</strong>
            {summary.charityCount} 場
            {summary.waitlistCount > 0 && (
              <span className="tag warn" style={{ marginLeft: 6 }}>
                其中 {summary.waitlistCount} 場遞補中
              </span>
            )}
          </div>
          <div style={{ marginTop: 8 }}>
            <strong>已選場次：</strong>
          </div>
          <ul>
            {summary.events.length === 0 && <li className="muted">（尚未選擇任何場次）</li>}
            {summary.events.map((e) => (
              <li key={e.id}>
                {e.eventDate ? e.eventDate : "日期待確認"}｜{e.title}
                {e.location ? `｜${e.location}` : ""}｜認列 {e.creditHours} 小時
                {e.waitlistPosition > 0 ? (
                  <span className="tag warn" style={{ marginLeft: 6 }}>
                    遞補 {e.waitlistPosition}
                  </span>
                ) : (
                  <span className="tag ok" style={{ marginLeft: 6 }}>
                    正取
                  </span>
                )}
              </li>
            ))}
          </ul>
          {summary.waitlistCount > 0 && (
            <p className="muted" style={{ marginTop: 8 }}>
              遞補場次表示該身分名額已滿，若前方有人取消才會遞補上；建議回上一步多勾其他還有名額的場次。
            </p>
          )}
        </div>

        <div className="spacer" />
        <button type="button" className="btn ghost full" onClick={onBack}>
          返回修改
        </button>
      </div>
    </div>
  );
}
